// Saved views as tabs above a list (Dean, 15 Oct 2026): pinned default first and marked, personal + shared, per-tab menu.
import { useState } from "react";
import { Pin, Users, MoreHorizontal, Plus, Save, Copy, Pencil, Trash2, Share2, Lock, Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function ViewTabs({ lv, allLabel = "All", testid = "views", className = "" }) {
  const [dialog, setDialog] = useState(null);   // {kind: "new"|"rename", view?}
  const { views, activeView, dirty, canPublish } = lv;
  return (
    <div className={`flex items-center gap-1 flex-wrap text-xs ${className}`} data-testid={testid}>
      <Tab active={!activeView} onClick={lv.clearView} testid={`${testid}-tab-all`}><Bookmark className="w-3 h-3 text-slate-400" /> {allLabel}</Tab>
      {views.map((v) => (
        <span key={v.view_id} className="inline-flex items-center">
          <Tab active={activeView?.view_id === v.view_id} onClick={() => lv.applyView(v)} testid={`${testid}-tab-${v.view_id}`} title={`${v.shared ? "Shared" : "Personal"}${v.owner_name ? ` · ${v.owner_name}` : ""}${v.pinned ? " · team default" : ""}`}>
            {v.pinned ? <Pin className="w-3 h-3 text-amber-600" /> : v.shared ? <Users className="w-3 h-3 text-sky-600" /> : <Lock className="w-3 h-3 text-slate-400" />}
            {v.name}
            {activeView?.view_id === v.view_id && dirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Unsaved changes" data-testid={`${testid}-dirty`} />}
          </Tab>
          <TabMenu v={v} lv={lv} canPublish={canPublish} onRename={() => setDialog({ kind: "rename", view: v })} testid={`${testid}-menu-${v.view_id}`} />
        </span>
      ))}
      {activeView && dirty && activeView.can_edit && (
        <Button size="sm" variant="outline" className="h-7 text-[11px] border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100" onClick={lv.saveChanges} data-testid={`${testid}-save-changes`}>
          <Save className="w-3 h-3 mr-1" /> Save changes
        </Button>
      )}
      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setDialog({ kind: "new" })} data-testid={`${testid}-save-new`}>
        <Plus className="w-3 h-3 mr-1" /> Save view
      </Button>
      {dialog && <NameDialog dialog={dialog} canPublish={canPublish} onClose={() => setDialog(null)}
        onSave={async (name, shared) => { if (dialog.kind === "new") await lv.saveNew({ name, shared }); else await lv.rename(dialog.view, name); setDialog(null); }} testid={testid} />}
    </div>
  );
}

function Tab({ active, onClick, children, testid, title }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-pressed={active}
      className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border transition-colors ${active ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
      data-testid={testid}>{children}</button>
  );
}

function TabMenu({ v, lv, canPublish, onRename, testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="h-7 w-5 inline-flex items-center justify-center text-slate-400 hover:text-slate-800" aria-label={`Options for ${v.name}`} data-testid={testid}><MoreHorizontal className="w-3.5 h-3.5" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="text-xs">
        {v.can_edit && <DropdownMenuItem onClick={onRename} data-testid={`${testid}-rename`}><Pencil className="w-3 h-3 mr-2" /> Rename</DropdownMenuItem>}
        <DropdownMenuItem onClick={() => lv.duplicate(v)} data-testid={`${testid}-duplicate`}><Copy className="w-3 h-3 mr-2" /> Duplicate</DropdownMenuItem>
        {canPublish && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => lv.share(v, !v.shared)} data-testid={`${testid}-share`}><Share2 className="w-3 h-3 mr-2" /> {v.shared ? "Make personal" : "Share with the team"}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => lv.pin(v, !v.pinned)} data-testid={`${testid}-pin`}><Pin className="w-3 h-3 mr-2" /> {v.pinned ? "Unpin default" : "Pin as team default"}</DropdownMenuItem>
          </>
        )}
        {v.can_edit && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-rose-600" onClick={() => { if (window.confirm(`Delete the view “${v.name}”?`)) lv.remove(v); }} data-testid={`${testid}-delete`}><Trash2 className="w-3 h-3 mr-2" /> Delete</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NameDialog({ dialog, canPublish, onClose, onSave, testid }) {
  const [name, setName] = useState(dialog.view?.name || "");
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => { if (!name.trim()) return; setBusy(true); try { await onSave(name.trim(), shared); } finally { setBusy(false); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-base">{dialog.kind === "new" ? "Save this view" : "Rename view"}</DialogTitle>
          <DialogDescription className="text-xs">{dialog.kind === "new" ? "Filters, sort and toggles as they are now. Personal unless you share it." : "Only the name changes."}</DialogDescription>
        </DialogHeader>
        <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My week" onKeyDown={(e) => e.key === "Enter" && submit()} data-testid={`${testid}-name-input`} />
        {dialog.kind === "new" && canPublish && (
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox checked={shared} onCheckedChange={(v) => setShared(!!v)} data-testid={`${testid}-share-check`} /> Share with the whole team
          </label>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid={`${testid}-dialog-cancel`}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy || !name.trim()} data-testid={`${testid}-dialog-save`}>{dialog.kind === "new" ? "Save view" : "Rename"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
